import {
  type UseMutationOptions,
  type UseQueryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import type { DTO } from "@equidefi/shared/types/dto";
import AgreementClient from "../clients/AgreementClient";
import { type ApiClientClass, useApi } from "./useApi";

type AgreementHistoryResponse = {
  data: DTO.AgreementHistoryListItem[];
  pagination: DTO.Pagination;
};

export type AgreementPreviewSigner = { name: string; email: string };

export type AgreementPreviewArgs = {
  agreementId: string;
  signers: AgreementPreviewSigner[];
};

export const useOfferingAgreements = (offeringId, options = {}) => {
  const agreementApi = useApi(AgreementClient as ApiClientClass<AgreementClient>);

  return useQuery(["offering", offeringId, "offering_agreements"], () => agreementApi.offeringAgreements(offeringId), {
    enabled: !!offeringId,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    ...options,
  });
};

export const useDeleteOfferingAgreement = (
  id: string,
  options: Omit<UseMutationOptions<unknown, unknown, unknown, unknown>, "mutationFn"> = {},
) => {
  const agreementApi = useApi(AgreementClient as ApiClientClass<AgreementClient>);

  return useMutation(async (agreementId: string) => agreementApi.deleteOfferingAgreement(id, agreementId), options);
};

export const useCreateOfferingAgreement = (
  id: string,
  options: Omit<UseMutationOptions<unknown, unknown, unknown, unknown>, "mutationFn"> = {},
) => {
  const agreementApi = useApi(AgreementClient as ApiClientClass<AgreementClient>);

  return useMutation(async (data: Record<string, unknown>) => agreementApi.createOfferingAgreement(id, data), options);
};

export const useUpdateOfferingAgreement = (id: string, offeringAgreementId: string, options = {}) => {
  const agreementAPi = useApi(AgreementClient as ApiClientClass<AgreementClient>);

  return useMutation(
    async (data: Record<string, unknown>) => agreementAPi.updateOfferingAgreement(id, offeringAgreementId, data),
    { ...options },
  );
};

export const usePublishOfferingAgreement = (
  offeringId: string,
  options: Omit<UseMutationOptions<unknown, unknown, string, unknown>, "mutationFn"> = {},
) => {
  const agreementApi = useApi(AgreementClient as ApiClientClass<AgreementClient>);

  return useMutation(
    async (agreementId: string) => agreementApi.publishOfferingAgreement(offeringId, agreementId),
    options,
  );
};

export const useCreateDraftFromOfferingAgreement = (
  offeringId: string,
  options: Omit<UseMutationOptions<unknown, unknown, string, unknown>, "mutationFn"> = {},
) => {
  const agreementApi = useApi(AgreementClient as ApiClientClass<AgreementClient>);

  return useMutation(
    async (agreementId: string) => agreementApi.createDraftFromOfferingAgreement(offeringId, agreementId),
    options,
  );
};

export const useCreateOfferingAgreementPreview = (
  offeringId: string,
  options: Omit<UseMutationOptions<unknown, unknown, AgreementPreviewArgs, unknown>, "mutationFn"> = {},
) => {
  const agreementApi = useApi(AgreementClient as ApiClientClass<AgreementClient>);

  return useMutation(
    async ({ agreementId, signers }: AgreementPreviewArgs) =>
      agreementApi.createOfferingAgreementPreview(offeringId, agreementId, signers),
    options,
  );
};

export const useAgreementPingNextSigner = (investmentId) => {
  const agreementApi = useApi(AgreementClient as ApiClientClass<AgreementClient>);

  return useMutation({
    mutationFn: () => agreementApi.pingNextSecondarySigner(investmentId),
  });
};

export const useAgreementHistory = (
  offeringId: string,
  agreementId: string,
  page = 1,
  perPage = 5,
  options: Omit<UseQueryOptions<AgreementHistoryResponse>, "queryKey" | "queryFn"> = {},
) => {
  const agreementApi = useApi(AgreementClient as ApiClientClass<AgreementClient>);

  const queryKey: readonly unknown[] = ["offering", offeringId, "agreement", agreementId, "history", page, perPage];

  return useQuery<AgreementHistoryResponse>(
    queryKey,
    () => agreementApi.agreementHistory(offeringId, agreementId, page, perPage),
    {
      enabled: !!agreementId,
      refetchOnMount: true,
      refetchOnWindowFocus: false,
      ...options,
    },
  );
};

export const useCountersignInvestment = (investmentId: string, options = {}) => {
  const agreementApi = useApi(AgreementClient as ApiClientClass<AgreementClient>);

  return useMutation(() => agreementApi.countersignInvestment(investmentId), {
    ...options,
  });
};

export const useAgreementHistoryDocument = (
  offeringId: string,
  agreementId: string,
  historyId: string,
  options: Omit<UseQueryOptions<DTO.AgreementHistory>, "queryKey" | "queryFn"> = {},
) => {
  const agreementApi = useApi(AgreementClient as ApiClientClass<AgreementClient>);

  const queryKey: readonly unknown[] = [
    "offering",
    offeringId,
    "agreement",
    agreementId,
    "history",
    historyId,
    "document",
  ];

  return useQuery<DTO.AgreementHistoryDocument>(
    queryKey,
    () => agreementApi.agreementHistoryDocument(offeringId, agreementId, historyId),
    {
      enabled: !!offeringId && !!agreementId && !!historyId,
      refetchOnWindowFocus: false,
      ...options,
    },
  );
};

export const useCreateAgreementHistory = (
  offeringId: string,
  agreementId: string,
  options: Omit<UseMutationOptions<unknown, unknown, unknown, unknown>, "mutationFn"> = {},
) => {
  const agreementApi = useApi(AgreementClient as ApiClientClass<AgreementClient>);
  const queryClient = useQueryClient();

  return useMutation(
    async (data: { document?: object; diff?: object; action?: string }) =>
      agreementApi.createAgreementHistory(offeringId, agreementId, data),
    {
      ...options,
      onSuccess: (data, variables, context) => {
        queryClient.invalidateQueries(["offering", offeringId, "agreement", agreementId, "history"]);
        options.onSuccess?.(data, variables, context);
      },
    },
  );
};

/**
 * @param {string} id
 * @param {import("@tanstack/react-query").QueryObserverOptions} options
 */
export const useInvestmentSignatures = (id, options = {}) => {
  const agreementApi = useApi(AgreementClient as ApiClientClass<AgreementClient>);

  return useQuery(["investment", id, "signatures"], () => agreementApi.investmentSignatures(id), {
    enabled: !!id,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    refetchOnMount: false,
    ...options,
  });
};
